// src/modules/auth/stores/auth.js
import { defineStore } from 'pinia';
import { useRouter } from 'vue-router';
// Важно: Здесь используется 'axios', который должен быть сконфигурирован глобально
// с перехватчиками (например, через src/plugins/axios.js).
import axios from 'axios';

// ВАЖНО: Базовый URL API берется из переменной окружения Vite.
// Убедитесь, что в вашем файле .env (например, .env.local) строка VITE_API_BASE_URL
// НЕ заканчивается на слэш. Например: VITE_API_BASE_URL=https://sandbox.momsrecipes.fun:4443/api
// Если она заканчивается на слэш, то при объединении с путями, которые начинаются со слэша,
// возникнет двойной слэш (//), что может вызвать проблемы с перенаправлениями и CORS.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: JSON.parse(localStorage.getItem('user')) || null, // Данные пользователя, если есть в localStorage
        token: localStorage.getItem('authToken') || null, // Токен из localStorage
        loading: false, // Состояние загрузки
        error: null, // Ошибки
    }),
    getters: {
        // Проверяет, аутентифицирован ли пользователь (есть ли токен и данные пользователя)
        isAuthenticated: (state) => !!state.token && !!state.user,
        // Возвращает объект пользователя
        getUser: (state) => state.user,
        // Возвращает токен
        getToken: (state) => state.token,
    },
    actions: {
        /**
         * Попытка входа пользователя в систему.
         * @param {Object} credentials - Объект с данными для входа (email, password).
         * @returns {boolean} - true в случае успеха, false в случае ошибки.
         */
        async login(credentials) {
            this.loading = true;
            this.error = null;
            try {
                // ИЗМЕНЕНО: Теперь используем API_BASE_URL + '/login' (без завершающего слэша)
                // Если ваш API_BASE_URL в .env НЕ заканчивается на слэш,
                // и ваш бэкенд ожидает '/login' (без завершающего слэша).
                // Это приведет к https://sandbox.momsrecipes.fun:4443/api/login
                const response = await axios.post(`${API_BASE_URL}/login`, credentials);

                this.token = response.data.access_token || response.data.token; // Проверяем оба варианта
                localStorage.setItem('authToken', this.token);

                if (response.data.user) {
                    this.user = response.data.user;
                    localStorage.setItem('user', JSON.stringify(this.user));
                } else if (response.data.customer) { // Добавлено, если ваш API возвращает 'customer' вместо 'user'
                    this.user = response.data.customer;
                    localStorage.setItem('user', JSON.stringify(this.user));
                } else {
                    // Если API логина не возвращает user/customer, делаем отдельный запрос
                    await this.fetchUser();
                }

                // Перенаправляем на главную страницу после успешного входа
                const router = useRouter();
                // Убедитесь, что 'main.home' - это правильное имя вашего маршрута после логина
                router.push({ name: 'main.home' }); 
                return true; // Успешный вход
            } catch (err) {
                console.error('Ошибка входа:', err.response?.data || err.message);
                this.error = err.response?.data?.message || 'Неверные учетные данные или ошибка сервера.';
                this.clearAuthData(); // Очищаем данные при ошибке
                return false; // Ошибка входа
            } finally {
                this.loading = false;
            }
        },

        /**
         * Загружает данные текущего пользователя с бэкенда.
         */
        async fetchUser() {
            if (!this.token) {
                this.clearAuthData(); // Нет токена - значит, не аутентифицирован
                return;
            }
            this.loading = true;
            this.error = null;
            try {
                // ИЗМЕНЕНО: Убрал завершающий слэш из `/user/` на `/user`, так как /api/user/ редко используется
                // для получения информации о текущем пользователе. Обычно /api/user или /api/me.
                // Также ИЗМЕНЕНО: 'Authorization': `Token ${this.token}` на `Bearer ${this.token}`
                const response = await axios.get(`${API_BASE_URL}/user`, { 
                    headers: {
                        'Authorization': `Bearer ${this.token}`, 
                    },
                });
                this.user = response.data;
                localStorage.setItem('user', JSON.stringify(this.user));
            } catch (err) {
                console.error('Ошибка получения данных пользователя:', err.response?.data || err.message);
                this.error = 'Не удалось загрузить данные пользователя.';
                // При ошибке токена или данных - вызываем обработчик неавторизованного доступа
                this.handleUnauthorized(); 
            } finally {
                this.loading = false;
            }
        },

        /**
         * Выход пользователя из системы.
         */
        async logout() {
            // Опционально: API-вызов к бэкенду для отзыва токена
            // В вашем случае, бэкенд уже отзывает старые токены при новом входе,
            // так что этот вызов может быть не строго обязателен для решения проблемы деавторизации.
            // Но это хорошая практика для явного выхода.
            try {
                if (this.token) {
                    // Пример: await axios.post(`${API_BASE_URL}/logout`, null, { 
                    //     headers: {
                    //         'Authorization': `Bearer ${this.token}`,
                    //     },
                    // });
                }
            } catch (err) {
                console.error('Ошибка при выходе из системы (API):', err);
                // Не блокируем выход из системы на фронтенде, даже если API-вызов не удался
            } finally {
                this.clearAuthData();
                // Перенаправляем на страницу входа
                const router = useRouter(); 
                // Убедитесь, что 'login' - это правильное имя вашего маршрута
                router.push({ name: 'login' }); 
            }
        },

        /**
         * Очищает все данные аутентификации.
         */
        clearAuthData() {
            this.user = null;
            this.token = null;
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            // Если вы используете refresh_token, его также нужно удалить
            // localStorage.removeItem('refreshToken');
        },

        /**
         * *** НОВОЕ ДЕЙСТВИЕ ***
         * Обрабатывает ситуации, когда доступ не авторизован (например, токен недействителен).
         * Это действие будет вызываться глобальным перехватчиком Axios.
         */
        handleUnauthorized() {
            console.warn('Обнаружен несанкционированный доступ или истечение сессии. Выполняю выход.');
            this.clearAuthData();
            const router = useRouter();
            // Перенаправляем на страницу логина, если мы еще не на ней
            // Убедитесь, что 'login' - это правильное имя вашего маршрута
            if (router.currentRoute.name !== 'login') { 
                router.push({ name: 'login' });
            }
        },

        /**
         * Инициализация аутентификации при старте приложения.
         * Проверяет наличие токена и пытается получить данные пользователя, если их нет.
         */
        async initializeAuth() {
            // Если есть токен, но данные пользователя еще не загружены
            if (this.token && !this.user) {
                await this.fetchUser();
            }
        }
    },
});
